import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, FolderOpen } from "lucide-react";

export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", season: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/collections", { ...form, kind: form.season.trim() ? "season" : "general" });
      toast.success("Collection created");
      setShowCreate(false);
      setForm({ name: "", description: "", season: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Curation</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Collections</h1>
          <p className="text-zinc-500 mt-2 text-sm">Curate selected outfits into collections and export lookbooks.</p>
        </div>
        <button data-testid="collections-create-button" onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New collection
        </button>
      </div>

      {collections.length === 0 && (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400" data-testid="collections-empty-state">
          <FolderOpen size={26} className="mx-auto mb-3" />
          <p className="text-sm">No collections yet — create one and add designs from the gallery.</p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {collections.map((c) => (
          <Link key={c.id} to={`/collections/${c.id}`} data-testid={`collection-card-${c.id}`}
            className="border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-sm hover:-translate-y-1 transition-transform duration-200">
            <div className="grid grid-cols-2 gap-px bg-zinc-100 h-40">
              {[0, 1, 2, 3].map((i) =>
                c.covers[i] ? (
                  <img key={i} src={imgUrl(c.covers[i])} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div key={i} className="bg-zinc-50" />
                )
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2">
                <p className="font-heading font-semibold truncate min-w-0">{c.name}</p>
                {c.kind === "ideas" && <span className="shrink-0 text-[9px] uppercase tracking-[0.1em] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">Ideas</span>}
                {c.kind === "season" && c.season && <span className="shrink-0 text-[9px] uppercase tracking-[0.1em] bg-zinc-900 text-white rounded px-1.5 py-0.5">{c.season}</span>}
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">{c.item_count} look{c.item_count !== 1 ? "s" : ""} · by {c.owner_name}</p>
            </div>
          </Link>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="collection-create-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New collection</h2>
              <button data-testid="collection-create-close" onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="collection-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Collection name — e.g. SS26 Resort"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <textarea data-testid="collection-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Description (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <input data-testid="collection-season-input" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="Season (optional) — e.g. Spring/Summer 2026"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button data-testid="collection-save-button" onClick={create} disabled={!form.name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating…" : "Create collection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
